import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Typography,
  makeStyles,
  Collapse,
  IconButton,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CustomEmailSectionRow from './CustomEmailSectionRow';
import { CUSTOM_EMAIL_SECTION_FIELDS } from './constants';
import useMetadataUpdate from '../../../../../../hooks/useMetadataUpdate';
import { useQuery } from 'react-fetching-library';
import { formattedAccountsValues } from '../../../../helpers';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: 1400,
    width: '100%',
  },
  headerSection: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3, 0),
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(1),
  },
  headerDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  categoryHeader: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.colors.lightBlue}`,
    '&:first-child': {
      marginTop: 0,
    },
  },
  messageTypeGrid: {
    marginBottom: theme.spacing(4),
  },
  typeRow: {
    marginBottom: theme.spacing(3),
  },
  typeHeader: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    background: 'rgba(33, 150, 243, 0.05)',
    borderRadius: 8,
    border: `1px solid ${theme.colors.lightBlue}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      background: 'rgba(33, 150, 243, 0.08)',
      borderColor: theme.colors.blue2,
    },
  },
  expandIcon: {
    color: theme.colors.blue2,
    transition: 'transform 0.2s ease-in-out',
  },
  typeCardsRow: {
    display: 'grid',
    gap: theme.spacing(2),
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  messageCard: {
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      borderColor: theme.colors.lightBlue,
    },
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  messageTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.colors.blue2,
    textTransform: 'capitalize',
  },
  customToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  toggleLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  languageContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  languageField: {
    flex: 1,
  },
  fieldLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  textArea: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'rgba(248, 250, 252, 0.8)',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        '& fieldset': {
          borderColor: theme.colors.lightBlue,
          borderWidth: 2,
        },
      },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  disabledOverlay: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(248, 250, 252, 0.7)',
      borderRadius: 8,
      zIndex: 1,
      pointerEvents: 'none',
    },
  },
}));

const CustomEmailSection = ({
                              form,
                              isDistributionSponsor,
                              backendErrors,
                              setAccount,
                            }) => {
  const classes = useStyles();
  const { en: { forms } } = useSelector((state) => state.i18n);
  const [defaultText, setDefaultText] = useState();
  const [expandedSections, setExpandedSections] = useState({});

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/default-text`,
    },
    false,
  ]);

  useEffect(() => {
    query().then(({ error, payload }) => {
      if (!error) {
        const findEmailOrSmsText = (firstKey, secondKey, local) => {
          return payload?.[firstKey]?.[secondKey]?.find(
            (e) => e.language === local,
          )?.text;
        };
        setDefaultText({
          emailSurveyInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'english'),
          emailSurveyInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'spanish'),
          emailResourceInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'english'),
          emailResourceInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'spanish'),
          emailSurveyReminderEnglish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'english'),
          emailSurveyReminderSpanish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'spanish'),
          emailResourceReminderEnglish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'english'),
          emailResourceReminderSpanish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'spanish'),
          emailSurveyCompletionEnglish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'english'),
          emailSurveyCompletionSpanish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'spanish'),

          smsSurveyInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'english'),
          smsSurveyInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'spanish'),
          smsResourceInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'english'),
          smsResourceInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'spanish'),
          smsSurveyReminderEnglish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'english'),
          smsSurveyReminderSpanish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'spanish'),
          smsResourceReminderEnglish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'english'),
          smsResourceReminderSpanish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'spanish'),
          smsSurveyCompletionEnglish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'english'),
          smsSurveyCompletionSpanish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'spanish'),
        });
      }
    });
  }, [query]);

  useEffect(() => {
    if (defaultText) {
      let newTextForFields = {};
      CUSTOM_EMAIL_SECTION_FIELDS.forEach(field => {
        if (
          !form.values[field.firstFieldName] ||
          form.values[field.idDefaultName]
        ) {
          newTextForFields[field.firstFieldName] = defaultText[field.firstFieldName];
        }
        if (
          !form.values[field.secondFieldName] ||
          form.values[field.idDefaultName]
        ) {
          newTextForFields[field.secondFieldName] = defaultText[field.secondFieldName];
        }
      });

      form.setValues({
        ...form.values,
        ...newTextForFields,
      });

      if (setAccount) {
        setAccount(formattedAccountsValues({
          ...form.initialValues,
          ...newTextForFields,
        }));
      }
    }
  }, [defaultText]);

  const groupedFields = {
    invitation: CUSTOM_EMAIL_SECTION_FIELDS.filter(field => field.rowNameSecond.includes('invitation')),
    reminder: CUSTOM_EMAIL_SECTION_FIELDS.filter(field => field.rowNameSecond.includes('reminder')),
    completion: CUSTOM_EMAIL_SECTION_FIELDS.filter(field => field.rowNameSecond.includes('completion')),
  };

  const groupFieldsByType = (fields) => {
    const grouped = {};
    fields.forEach(field => {
      const type = field.rowNameFirst; // 'survey' или 'resource'
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(field);
    });
    return grouped;
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerSection}>
        <Typography className={classes.headerTitle}>
          {forms.customEmailConfiguration || 'Custom Email & SMS Configuration'}
        </Typography>
        <Typography className={classes.headerDescription}>
          Customize the messages sent to your patients for different communication types.
          Use the toggle to override default messages with your own content.
        </Typography>
      </div>

      {Object.entries(groupedFields).map(([categoryKey, fields]) => {
        const fieldsByType = groupFieldsByType(fields);
        
        return (
          <div key={categoryKey}>
            <Typography className={classes.categoryHeader}>
              {categoryKey === 'invitation' ? 'Invitations' : 
               categoryKey === 'reminder' ? 'Reminders' : 'Completion Notifications'}
            </Typography>
            
            <div className={classes.messageTypeGrid}>
              {Object.entries(fieldsByType).map(([type, typeFields]) => {
                const sectionKey = `${categoryKey}-${type}`;
                const isExpanded = expandedSections[sectionKey] || false;
                
                return (
                  <div key={type} className={classes.typeRow}>
                    <div 
                      className={classes.typeHeader}
                      onClick={() => setExpandedSections(prev => ({
                        ...prev,
                        [sectionKey]: !prev[sectionKey]
                      }))}
                    >
                      <Typography component="span">
                        {type === 'survey' ? 'Assessment' : 'Resource'} Communications
                      </Typography>
                      <IconButton size="small" className={classes.expandIcon}>
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </div>
                    
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <div className={classes.typeCardsRow}>
                        {typeFields.map(field => (
                          <div key={field.firstFieldName} className={classes.messageCard}>
                            <div className={classes.messageHeader}>
                              <Typography className={classes.messageTitle}>
                                {field.rowNameSecond.includes('Email') ? 'Email' : 'SMS'}
                              </Typography>
                              <div className={classes.customToggle}>
                                <Typography className={classes.toggleLabel}>
                                  Custom
                                </Typography>
                              </div>
                            </div>
                            
                            <CustomEmailSectionRow
                              isDistributionSponsor={isDistributionSponsor}
                              rowName={forms[field.rowNameFirst] + ' ' + forms[field.rowNameSecond]}
                              firstFieldName={field.firstFieldName}
                              secondFieldName={field.secondFieldName}
                              idDefaultName={field.idDefaultName}
                              backendErrors={backendErrors}
                              defaultFirstFieldText={defaultText ? defaultText[field.firstFieldName] : null}
                              defaultSecondFieldText={defaultText ? defaultText[field.secondFieldName] : null}
                            />
                          </div>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

CustomEmailSection.propTypes = {
  tradeName: PropTypes.string,
  businessName: PropTypes.string,
  isCurrentAccountEditing: PropTypes.bool,
  isDistributionSponsor: PropTypes.bool,
};

CustomEmailSection.defaultProps = {
  tradeName: '',
  businessName: '',
  isCurrentAccountEditing: false,
  isDistributionSponsor: false,
};

export default CustomEmailSection;

